import history from '@AppRoutes/history';
import Auth from '@Services/Auth';
import PendoService from '@Services/Pendo';
import UsabillaService from '@Services/Usabilla';
import { PENDO_URL } from '@Constants/BuildInfo';
import AppStorage from '@Services/AppStorage';
import HttpRequest from '@Services/RequestFactory';
import loginConstants from '@Services/Login/LoginConstants';
import WindowProxy from '@Services/WindowProxy';
import Catalyst from '@Services/Catalyst';
import NavigationHistory from '@Services/NavigationHistory';
import pick from 'lodash/pick';
import extend from 'lodash/extend';
import find from 'lodash/find';

import RequestUrls from '@Constants/RequestUrlsConstants';
import MessageBroker from '@Services/MessageBroker';

import {
  institutionSetActualDataReducerType,
  institutionSetInstitutionsForSearchReducerType,
  institutionSetInstitutionsGroupsReducerType,
  institutionSetIpAddressReducerType,
  institutionSetIsLoadingReducerType,
  institutionSetNotificationsReducerType,
  institutionResetStateReducerType,
} from './InstitutionConstants';

import { actualData as actualDataSelector, institutionsGroups as institutionsGroupsSelector } from './InstitutionSelectors';

export function setIsLoading(isLoading) {
  return {
    type: institutionSetIsLoadingReducerType,
    payload: { isLoading },
  };
}

export function setIpAddress(ipAddress) {
  return {
    type: institutionSetIpAddressReducerType,
    payload: { ipAddress },
  };
}

export function setInstitutionsForSearch(institutionsForSearch) {
  return {
    type: institutionSetInstitutionsForSearchReducerType,
    payload: { institutionsForSearch },
  };
}

export function setInstitutionsGroups(institutionsGroups) {
  return {
    type: institutionSetInstitutionsGroupsReducerType,
    payload: { institutionsGroups },
  };
}

export function setNotifications(notifications) {
  return {
    type: institutionSetNotificationsReducerType,
    payload: { notifications },
  };
}

export function setActualData(actualData) {
  return {
    type: institutionSetActualDataReducerType,
    payload: { actualData },
  };
}

export function cancel() {
  return async () => {
    const stateWithParams = NavigationHistory.getLastVisitedState(...loginConstants.STATE_WHITELIST);
    if (stateWithParams) {
      history.push({
        pathname: stateWithParams.pathName,
        state: stateWithParams.params,
      });
    } else {
      Auth.goToLandingPage();
    }
  };
}

export function login() {
  return async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    const actualData = actualDataSelector(getState());

    if (actualData.institution && actualData.callbackUrl) {
      // save last selected institution
      AppStorage.instance.set('lastSelectedInstitution', pick(actualData, 'institution', 'callbackUrl'));
    }
    if (actualData.institution && actualData.rememberChoice) {
      const temp = pick(actualData, 'group', 'institution', 'rememberChoice', 'callbackUrl');
      // save data for two selects
      AppStorage.instance.set('userInstitutionInfo', temp);
    } else {
      AppStorage.instance.remove('userInstitutionInfo');
    }
    WindowProxy.location.href = actualData.callbackUrl;
  };
}

export function getInstitutionsForSearch() {
  return async (dispatch, getState) => {
    const institutionsGroups = institutionsGroupsSelector(getState());

    institutionsGroups.forEach((group) => {
      if (group.institutions && group.institutions.length) {
        dispatch(setInstitutionsForSearch(group.institutions));
      }
    });
  };
}

export const init = () => async (dispatch) => {
  const notifications = Auth.getUserNotifications();
  dispatch(setNotifications(notifications));

  Catalyst.trackSimpleEvent('loginStart');

  UsabillaService.init();

  await PendoService.link(PENDO_URL)
    .then(() => PendoService.updateVisitor())
    .catch(Logger.warn);

  const userData = AppStorage.instance.get('userInstitutionInfo');
  const isRemember = userData && userData.rememberChoice;

  await MessageBroker.send(RequestUrls.institutions, null, { Authorization: false })
    .then(async (data) => {
      dispatch(setInstitutionsGroups(data));
      dispatch(getInstitutionsForSearch());
      if (isRemember) {
        const currentGroup = find(data, (group) => group.description === userData.group);

        dispatch(
          setActualData({
            group: userData.group,
            institution: userData.institution,
            rememberChoice: userData.rememberChoice,
            callbackUrl: userData.callbackUrl,
            currentInstitutions: currentGroup.institutions.length ? currentGroup.institutions : [],
          })
        );
      }
    })
    .catch(Logger.error);

  await Auth.getClientInfo()
    .then((data) => {
      dispatch(setIpAddress(data.ipAddress));
    })
    .catch(Logger.error);
};

export function goToLastSelected(href) {
  return async (dispatch) => {
    dispatch(setIsLoading(true));
    WindowProxy.location.href = href;
  };
}

export function selectGroup(group) {
  return async (dispatch, getState) => {
    const actualData = actualDataSelector(getState());

    dispatch(
      setActualData({
        ...actualData,
        group: group?.description,
        institution: '',
        currentInstitutions: group?.institutions.length ? group.institutions : [],
      })
    );
  };
}

export function selectInstitution(institution, data) {
  return async (dispatch, getState) => {
    let url = HttpRequest.getUrlWithPrefix('/oauth/shibboleth-sso');
    if (url.charAt(0) === '/') {
      url = WindowProxy.location.origin + url;
    }
    url = encodeURIComponent(url);

    if (data) {
      extend(data, {
        institution: institution.description,
        callbackUrl: institution.externalUrl + url,
      });

      return;
    }

    const actualData = actualDataSelector(getState());

    dispatch(
      setActualData({
        ...actualData,
        institution: institution.description,
        callbackUrl: institution.externalUrl + url,
      })
    );
  };
}

export function selectSearchedInstitution(institution) {
  return async (dispatch) => {
    dispatch(setIsLoading(true));
    const lastSelected = {};
    await dispatch(selectInstitution(institution, lastSelected));
    AppStorage.instance.set('lastSelectedInstitution', lastSelected);
    WindowProxy.location.href = lastSelected.callbackUrl;
  };
}

export function changeRememberMe(value) {
  return async (dispatch, getState) => {
    const actualData = actualDataSelector(getState());

    dispatch(
      setActualData({
        ...actualData,
        rememberChoice: value,
      })
    );
  };
}

export function resetState() {
  return {
    type: institutionResetStateReducerType,
  };
}
